import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { formatDate } from '@/utils/dateUtils';
import { Customer } from '@/types';

interface CustomerTableProps {
  customers: Customer[];
  onEdit: (customer: Customer) => void;
  onServiceConnection: (customer: Customer) => void;
  onDeactivate: (customerId: number) => void;
  onActivate: (customerId: number) => void;
  onDelete: (customerId: number) => void;
}

export const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  onEdit,
  onServiceConnection,
  onDeactivate,
  onActivate,
  onDelete,
}) => {
  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap">ID</TableHead>
            <TableHead className="whitespace-nowrap">회사명</TableHead>
            <TableHead className="whitespace-nowrap">고객사 코드</TableHead>
            <TableHead className="whitespace-nowrap">별칭</TableHead>
            <TableHead className="whitespace-nowrap">플랜</TableHead>
            <TableHead className="whitespace-nowrap">사용자</TableHead>
            <TableHead className="whitespace-nowrap">계약 종료일</TableHead>
            <TableHead className="whitespace-nowrap">상태</TableHead>
            <TableHead className="whitespace-nowrap">생성일</TableHead>
            <TableHead className="whitespace-nowrap">담당자</TableHead>
            <TableHead className="whitespace-nowrap text-right">작업</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((customer) => (
            <TableRow
              key={customer.id}
              className={customer.is_deleted ? 'opacity-50 bg-gray-50' : ''}
            >
              <TableCell className="whitespace-nowrap text-sm text-gray-600">
                {customer.id}
              </TableCell>
              <TableCell className="font-medium whitespace-nowrap">
                {customer.customer_name}
                {customer.is_deleted && <span className="ml-2 text-xs text-red-500">(삭제됨)</span>}
              </TableCell>
              <TableCell className="whitespace-nowrap">{customer.customer_code}</TableCell>
              <TableCell className="whitespace-nowrap">{customer.alias || '-'}</TableCell>
              <TableCell className="whitespace-nowrap">{customer.plan?.plan_name || '플랜 없음'}</TableCell>
              <TableCell className="whitespace-nowrap">{customer.active_user_count ?? 0}명 / {customer.max_users}명</TableCell>
              <TableCell className="whitespace-nowrap">{customer.contract_end_date || '-'}</TableCell>
              <TableCell className="whitespace-nowrap">
                {customer.is_deleted ? (
                  <span className="text-xs text-red-500">삭제됨</span>
                ) : !customer.is_active ? (
                  <span className="text-xs text-yellow-600">비활성화</span>
                ) : (
                  <span className="text-xs text-green-600">활성화</span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {formatDate(customer.created_at)}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {customer.contact_name ? (
                  <div>
                    <div>{customer.contact_name}</div>
                    {customer.contact_email && (
                      <div className="text-xs text-gray-500">{customer.contact_email}</div>
                    )}
                  </div>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                <div className="flex space-x-1">
                  {!customer.is_deleted && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(customer)}
                        disabled={!customer.is_active}
                      >
                        수정
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onServiceConnection(customer)}
                        disabled={!customer.is_active}
                      >
                        서비스
                      </Button>
                      {customer.is_active ? (
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => onDeactivate(customer.id)}
                        >
                          비활성화
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onActivate(customer.id)}
                        >
                          활성화
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDelete(customer.id)}
                        disabled={customer.is_active}
                      >
                        삭제
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

